import React, { useEffect, useRef, useState } from "react";

import {
  CloseOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Alert, Button, Input } from "antd";
import Modal from "antd/es/modal/Modal";

import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";
import { shouldConvertSpecForUnitConversion } from "./helper";

import { convertUnit } from "../../../../../actions/reports";

import { ReactComponent as ArrowRightIcon } from "../../../../../assets/images/product/specmanager/arrow-right-line.svg";

import "./ConvertUnitModal.css";

const displayValue = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) {
    return "-";
  }
  return normalized;
};

/** Spec Manager display/payload: append unit when not already present (e.g. "Negative/25 g"). */
const appendUnitForDisplay = (value, unit) => {
  const normalizedValue = displayValue(value);
  const normalizedUnit = String(unit ?? "").trim();
  if (!normalizedUnit || normalizedUnit === "-") {
    return normalizedValue;
  }
  if (normalizedValue === "-") {
    return normalizedUnit;
  }
  if (normalizedValue.endsWith(normalizedUnit)) {
    return normalizedValue;
  }
  return `${normalizedValue} ${normalizedUnit}`;
};

const getSameUnitMessage = (unit) => (
  `This test already shows in ${displayValue(unit)}. Pick a different unit to convert.`
);

const isSameUnit = (currentUnit, targetUnit) => {
  const normalizedCurrent = String(currentUnit ?? "").trim();
  const normalizedTarget = String(targetUnit ?? "").trim();
  if (!normalizedCurrent || normalizedCurrent === "-") {
    return false;
  }
  return normalizedTarget.toLowerCase() === normalizedCurrent.toLowerCase();
};

const modalTooltipContainer = (trigger) => (
  trigger?.closest(".ant-modal-body") || document.body
);

function TruncatedValue({ value }) {
  const text = displayValue(value);
  return (
    <StyledTooltip
      title={text}
      placement="top"
      getPopupContainer={modalTooltipContainer}
    >
      <span className="ConvertUnitModal__Value">{text}</span>
    </StyledTooltip>
  );
}

function ConvertUnitModal({
  testName,
  testCategory,
  hasTestDetail,
  testDetailType,
  currentResult,
  currentSpec,
  currentUnit,
  specUnit,
  testDate,
  onClose,
  onUpdateResult,
  isSaving,
  specOnly = false,
}) {
  const [targetUnit, setTargetUnit] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [conversionErrors, setConversionErrors] = useState([]);
  const [pendingPreview, setPendingPreview] = useState(null);
  const convertRequestIdRef = useRef(0);
  const targetInputWrapRef = useRef(null);

  useEffect(() => () => {
    convertRequestIdRef.current += 1;
  }, []);

  const focusTargetInput = () => {
    window.setTimeout(() => {
      const inputNode = targetInputWrapRef.current?.querySelector("input");
      if (inputNode && typeof inputNode.focus === "function") {
        inputNode.focus();
      }
    }, 50);
  };

  const specValueToConvert = specOnly
    ? appendUnitForDisplay(currentSpec, currentUnit)
    : currentResult;
  const hasTargetUnit = Boolean(targetUnit.trim());
  const canConvert = hasTargetUnit && !isConverting;
  const canUpdateResult = Boolean(pendingPreview)
    && !pendingPreview?.isPartial
    && conversionErrors.length === 0
    && !isConverting
    && !isSaving;
  const showConversionAlerts = conversionErrors.length > 0 && !isConverting;
  const hasPreviewResultError = conversionErrors.some(
    (error) => error.source === "result",
  );
  const hasPreviewSpecError = conversionErrors.some(
    (error) => error.source === "spec",
  );
  const showInputError = conversionErrors.some(
    (error) => error.source === "same_unit"
      || error.source === "result"
      || (specOnly && error.source === "spec"),
  );

  const getPreviewValueColClassName = (hasError) => (
    `ConvertUnitModal__ValueCol${hasError ? " ConvertUnitModal__ValueCol--error" : ""}`
  );

  const getConversionErrorLabel = (source) => {
    if (specOnly && (source === "result" || source === "spec")) {
      return "Spec";
    }
    if (source === "result") {
      return "Result";
    }
    if (source === "spec") {
      return "Spec";
    }
    return null;
  };

  const handleClose = () => {
    convertRequestIdRef.current += 1;
    onClose();
  };

  const handleTargetUnitChange = (event) => {
    const nextValue = event.target.value;
    setTargetUnit(nextValue);
    setConversionErrors([]);
    if (
      pendingPreview
      && nextValue.trim().toLowerCase() !== String(pendingPreview.unit || "").trim().toLowerCase()
    ) {
      setPendingPreview(null);
    }
  };

  const handleSuggestedUnitSelect = (unit) => {
    setTargetUnit(unit);
    setConversionErrors([]);
    setPendingPreview(null);
  };

  const resolvePreviewValue = (response) => {
    if (!response?.success) {
      return null;
    }

    if (response?.result != null && response.result !== "") {
      return response.result;
    }

    return null;
  };

  const buildPendingPreview = ({
    resultResponse,
    specResponse,
    shouldConvertSpec,
    normalizedTarget,
    hasErrors,
  }) => {
    let specs = currentSpec;
    if (specOnly) {
      specs = resolvePreviewValue(resultResponse);
    } else if (shouldConvertSpec) {
      specs = resolvePreviewValue(specResponse);
    }
    return {
      result: resolvePreviewValue(resultResponse),
      specs,
      unit: resultResponse?.unit || specResponse?.unit || normalizedTarget,
      isPartial: hasErrors,
    };
  };

  const buildConversionError = (source, response) => {
    const message = response?.message || "Something went wrong. Please try again.";
    const isInfo = response?.status === "Info" || response?.status === "info";
    return {
      source,
      variant: isInfo ? "info" : "error",
      message,
      unit: response?.unit || null,
    };
  };

  const handleConvert = async () => {
    if (!canConvert) {
      return;
    }

    const normalizedTarget = targetUnit.trim();

    if (isSameUnit(currentUnit, normalizedTarget)) {
      setConversionErrors([{
        source: "same_unit",
        variant: "info",
        message: getSameUnitMessage(currentUnit),
        unit: null,
      }]);
      setPendingPreview(null);
      return;
    }

    const requestId = convertRequestIdRef.current + 1;
    convertRequestIdRef.current = requestId;
    setIsConverting(true);
    setConversionErrors([]);
    setPendingPreview(null);

    const shouldConvertSpec = !specOnly && shouldConvertSpecForUnitConversion({
      testCategory,
      currentSpec,
      specUnit,
      hasTestDetail,
      testDetailType,
    });

    const conversionRequests = [
      convertUnit({
        test_name: testName,
        result: specValueToConvert,
        conversion_unit: normalizedTarget,
      }),
    ];

    if (shouldConvertSpec) {
      conversionRequests.push(
        convertUnit({
          test_name: testName,
          result: currentSpec,
          conversion_unit: normalizedTarget,
        }),
      );
    }

    let resultResponse;
    let specResponse;

    try {
      const responses = await Promise.all(conversionRequests);
      [resultResponse] = responses;
      if (shouldConvertSpec) {
        [, specResponse] = responses;
      }
    } catch (error) {
      resultResponse = {
        success: false,
        status: "Error",
        message: "Something went wrong. Please try again.",
      };
    }

    if (convertRequestIdRef.current !== requestId) {
      return;
    }

    setIsConverting(false);

    const errors = [];

    if (!resultResponse?.success) {
      errors.push(buildConversionError(specOnly ? "spec" : "result", resultResponse));
    }

    if (shouldConvertSpec && !specResponse?.success) {
      errors.push(buildConversionError("spec", specResponse));
    }

    if (errors.length > 0) {
      setConversionErrors(errors);
    } else {
      setConversionErrors([]);
    }

    setPendingPreview(buildPendingPreview({
      resultResponse,
      specResponse,
      shouldConvertSpec,
      normalizedTarget,
      hasErrors: errors.length > 0,
    }));
  };

  const handleUpdateResult = () => {
    if (!canUpdateResult) {
      return;
    }
    onUpdateResult({
      test: testName,
      testDate,
      result: pendingPreview.result,
      specs: pendingPreview.specs,
      unit: pendingPreview.unit,
    });
  };

  const previewResult = pendingPreview ? displayValue(pendingPreview.result) : "-";
  const previewSpec = pendingPreview ? displayValue(pendingPreview.specs) : "-";
  const currentSpecDisplay = specOnly
    ? appendUnitForDisplay(currentSpec, currentUnit)
    : currentSpec;
  let previewSpecDisplay = previewSpec;
  if (specOnly) {
    previewSpecDisplay = pendingPreview
      ? appendUnitForDisplay(
        pendingPreview.result ?? pendingPreview.specs,
        pendingPreview.unit || targetUnit,
      )
      : "-";
  }

  const renderConversionAlertMessage = (error) => {
    const label = getConversionErrorLabel(error.source);
    const showLabel = label && (conversionErrors.length > 1 || Boolean(pendingPreview));

    return (
      <span className="ConvertUnitModal__AlertMessage">
        <span className="ConvertUnitModal__AlertMessageText">
          {showLabel && (
            <span className="ConvertUnitModal__AlertSource">{`${label}: `}</span>
          )}
          {error.message}
        </span>
        {error.variant !== "info" && error.unit && (
          <Button
            type="default"
            className="ConvertUnitModal__SuggestedUnitBtn"
            onClick={() => handleSuggestedUnitSelect(error.unit)}
          >
            {error.unit}
          </Button>
        )}
      </span>
    );
  };

  const renderSpecOnlyPanels = () => (
    <div className="ConvertUnitModal__Columns ConvertUnitModal__Columns--specOnly">
      <div className="ConvertUnitModal__Panel ConvertUnitModal__Panel--current ConvertUnitModal__Panel--specOnly">
        <div className="ConvertUnitModal__PanelTop ConvertUnitModal__PanelTop--specOnly">
          <div className="ConvertUnitModal__ValueCol">
            <div className="ConvertUnitModal__ValueStack">
              <span className="ConvertUnitModal__ValueLabel">Spec</span>
              <div className="ConvertUnitModal__ValueContent">
                <TruncatedValue value={currentSpecDisplay} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ArrowRightIcon className="ConvertUnitModal__Arrow" aria-hidden />

      <div className="ConvertUnitModal__Panel ConvertUnitModal__Panel--specOnly">
        <div className="ConvertUnitModal__PanelTop ConvertUnitModal__PanelTop--specOnly">
          <div className={getPreviewValueColClassName(hasPreviewSpecError)}>
            <div className="ConvertUnitModal__ValueStack">
              <span className="ConvertUnitModal__ValueLabel">Spec</span>
              <div className="ConvertUnitModal__ValueContent">
                <TruncatedValue value={previewSpecDisplay} />
              </div>
            </div>
          </div>
        </div>
        <div className="ConvertUnitModal__PanelBottom">
          <div
            ref={targetInputWrapRef}
            className="ConvertUnitModal__TargetInputWrap"
          >
            <Input
              className="ConvertUnitModal__TargetInput"
              status={showInputError ? "error" : undefined}
              value={targetUnit}
              onChange={handleTargetUnitChange}
              onPressEnter={handleConvert}
              disabled={isConverting}
              placeholder=""
              autoFocus
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSampleResultPanels = () => (
    <div className="ConvertUnitModal__Columns">
      <div className="ConvertUnitModal__Panel ConvertUnitModal__Panel--current">
        <div className="ConvertUnitModal__PanelTop">
          <div className="ConvertUnitModal__ValueCol">
            <div className="ConvertUnitModal__ValueStack">
              <span className="ConvertUnitModal__ValueLabel">Result</span>
              <div className="ConvertUnitModal__ValueContent">
                <TruncatedValue value={currentResult} />
              </div>
            </div>
          </div>
          <div className="ConvertUnitModal__Divider" />
          <div className="ConvertUnitModal__ValueCol">
            <div className="ConvertUnitModal__ValueStack">
              <span className="ConvertUnitModal__ValueLabel">Spec</span>
              <div className="ConvertUnitModal__ValueContent">
                <TruncatedValue value={currentSpec} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ArrowRightIcon className="ConvertUnitModal__Arrow" aria-hidden />

      <div className="ConvertUnitModal__Panel">
        <div className="ConvertUnitModal__PanelTop">
          <div className={getPreviewValueColClassName(hasPreviewResultError)}>
            <div className="ConvertUnitModal__ValueStack">
              <span className="ConvertUnitModal__ValueLabel">Result</span>
              <div className="ConvertUnitModal__ValueContent">
                <TruncatedValue value={previewResult} />
              </div>
            </div>
          </div>
          <div className="ConvertUnitModal__Divider" />
          <div className={getPreviewValueColClassName(hasPreviewSpecError)}>
            <div className="ConvertUnitModal__ValueStack">
              <span className="ConvertUnitModal__ValueLabel">Spec</span>
              <div className="ConvertUnitModal__ValueContent">
                <TruncatedValue value={previewSpec} />
              </div>
            </div>
          </div>
        </div>
        <div className="ConvertUnitModal__PanelBottom">
          <div
            ref={targetInputWrapRef}
            className="ConvertUnitModal__TargetInputWrap"
          >
            <Input
              className="ConvertUnitModal__TargetInput"
              status={showInputError ? "error" : undefined}
              value={targetUnit}
              onChange={handleTargetUnitChange}
              onPressEnter={handleConvert}
              disabled={isConverting}
              placeholder=""
              autoFocus
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      open
      closable={false}
      centered
      footer={null}
      onCancel={isConverting || isSaving ? undefined : handleClose}
      afterOpenChange={(open) => {
        if (open) {
          focusTargetInput();
        }
      }}
      wrapClassName={`ConvertUnitModal${specOnly ? " ConvertUnitModal--specOnly" : ""}`}
      className={`ConvertUnitModal${specOnly ? " ConvertUnitModal--specOnly" : ""}`}
      width={603}
      destroyOnClose
    >
      <div className="ConvertUnitModal__Header">
        <span className="ConvertUnitModal__Title">Convert unit</span>
        <CloseOutlined
          className="ConvertUnitModal__CloseIcon"
          aria-label="Close"
          onClick={() => {
            if (!isConverting && !isSaving) {
              handleClose();
            }
          }}
        />
      </div>

      <div className="ConvertUnitModal__Body">
        <div className="ConvertUnitModal__TestBanner">
          <StyledTooltip
            title={testName}
            placement="top"
            getPopupContainer={modalTooltipContainer}
          >
            <span className="ConvertUnitModal__TestName">{testName}</span>
          </StyledTooltip>
          <span className="ConvertUnitModal__TestLabel">Current Test</span>
        </div>

        <div className="ConvertUnitModal__Content">
          <div className="ConvertUnitModal__ConversionCard">
            <span className="ConvertUnitModal__Instruction">
              Enter a compatible unit.
            </span>

            {specOnly ? renderSpecOnlyPanels() : renderSampleResultPanels()}
          </div>

          <div className="ConvertUnitModal__ConvertAction">
            <Button
              type="default"
              onClick={handleConvert}
              disabled={!hasTargetUnit || isConverting}
              loading={isConverting}
            >
              {isConverting ? "Converting" : "Convert"}
            </Button>
          </div>

          {showConversionAlerts && (
            <div className="ConvertUnitModal__Alerts">
              {conversionErrors.map((error) => (
                <Alert
                  key={error.source}
                  className={`ConvertUnitModal__Alert${
                    error.variant === "info"
                      ? " ConvertUnitModal__Alert--info"
                      : " ConvertUnitModal__Alert--error"
                  }`}
                  type={error.variant === "info" ? "info" : "error"}
                  showIcon
                  icon={error.variant === "info" ? <InfoCircleOutlined /> : <WarningOutlined />}
                  message={renderConversionAlertMessage(error)}
                />
              ))}
            </div>
          )}

          {isConverting && (
            <Alert
              className="ConvertUnitModal__Alert ConvertUnitModal__Alert--progress"
              type="info"
              showIcon
              icon={<LoadingOutlined spin />}
              message={`Converting units to ‘${targetUnit.trim()}’...`}
            />
          )}
        </div>
      </div>

      <div className="ConvertUnitModal__Footer">
        <Button
          type="default"
          onClick={handleClose}
          disabled={isConverting || isSaving}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleUpdateResult}
          disabled={!canUpdateResult}
          loading={isSaving}
        >
          {specOnly ? "Update Specs" : "Update Result"}
        </Button>
      </div>
    </Modal>
  );
}

export default ConvertUnitModal;
